/**
 * JiraConfigCard - Jira configuration card
 *
 * Features:
 * - Display Jira project configuration
 * - Edit assignee and workflow statuses
 * - Show project details and workflow visualization
 */

import { createPortal } from 'react-dom';
import {User, ExternalLink, ArrowRight, X, Loader2} from 'lucide-react';
import type { JiraIntegrationState } from '@resolve-ai/shared';
import type { JiraUser, JiraStatus } from '@/lib/integrations-api.ts';
import { StatusBadge, Card, Dropdown } from '../common';
import {CpuIcon, EditIcon, JiraLogo} from '../icons';

interface JiraConfig {
  jiraProjectKey: string;
  jiraProjectName: string | null;
  jiraFilterJql: string | null;
  assignee: string | null;
  assigneeDisplayName: string | null;
  startStatus: string | null;
  endStatus: string | null;
}

interface JiraConfigCardProps {
  jiraConfig: JiraConfig | null;
  jiraIntegrationStatus: JiraIntegrationState | null;
  isAdmin: boolean;
  showEditModal: boolean;
  isLoadingUsers: boolean;
  isLoadingStatuses: boolean;
  isSaving: boolean;
  jiraAssignableUsers: JiraUser[];
  jiraStatuses: JiraStatus[];
  selectedAssigneeAccountId: string;
  selectedStartStatus: string;
  selectedEndStatus: string;
  validationError: string;
  onOpenEditModal: () => void;
  onCloseEditModal: () => void;
  onSave: () => void;
  onSetSelectedAssigneeAccountId: (accountId: string) => void;
  onSetSelectedStartStatus: (status: string) => void;
  onSetSelectedEndStatus: (status: string) => void;
  hasChanges: boolean;
}

export function JiraConfigCard({
  jiraConfig,
  jiraIntegrationStatus,
  isAdmin,
  showEditModal,
  isLoadingUsers,
  isLoadingStatuses,
  isSaving,
  jiraAssignableUsers,
  jiraStatuses,
  selectedAssigneeAccountId,
  selectedStartStatus,
  selectedEndStatus,
  validationError,
  onOpenEditModal,
  onCloseEditModal,
  onSave,
  onSetSelectedAssigneeAccountId,
  onSetSelectedStartStatus,
  onSetSelectedEndStatus,
  hasChanges,
}: JiraConfigCardProps) {
  const titleStyle='font-caption text-gray-500 dark:text-white-500 capitalize mb-[0.38rem]';
  const contentStyle='font-body-base text-gray-900 dark:text-white truncate';

  return (
    <>
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <JiraLogo className="w-4 h-4" />
            <h3 className="text-[1rem] leading-[140%] font-semibold text-gray-900 dark:text-white">
              Jira Configuration
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {jiraIntegrationStatus?.status && <StatusBadge status={jiraIntegrationStatus.status} size="lg" />}
            {isAdmin && jiraConfig && jiraIntegrationStatus?.status ==="connected" && (
              <button
                onClick={onOpenEditModal}
                className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Edit Jira configuration"
              >
                <EditIcon  />
              </button>
            )}
          </div>
        </div>
        {!jiraConfig ? (
          <p className="text-sm text-gray-600 dark:text-gray-400">No Jira project configured</p>
        ) : (
          <div className="space-y-4">
            {/* Project Info Row - 4 columns */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {jiraIntegrationStatus?.siteName && (
                <div className="min-w-0">
                  <p className={titleStyle}>Site</p>
                  <div className="flex items-center gap-2 min-w-0">
                    <p className={contentStyle} title={jiraIntegrationStatus.siteName}>
                      {jiraIntegrationStatus.siteName}
                    </p>
                    {jiraIntegrationStatus.siteUrl && jiraConfig.jiraProjectKey && (
                      <a
                        href={`${jiraIntegrationStatus.siteUrl}/browse/${jiraConfig.jiraProjectKey}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 shrink-0"
                        title="View in Jira"
                      >
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              )}
              <div className="min-w-0">
                <p className={titleStyle}>Project Key</p>
                <p className={contentStyle} title={jiraConfig.jiraProjectKey || 'Not set'}>
                  {jiraConfig.jiraProjectKey || 'Not set'}
                </p>
              </div>
              <div className="min-w-0">
                <p className={titleStyle}>Name</p>
                <p className={contentStyle} title={jiraConfig.jiraProjectName || 'Not set'}>
                  {jiraConfig.jiraProjectName || 'Not set'}
                </p>
              </div>
              {jiraConfig.assignee && (
                <div className="min-w-0">
                  <p className={titleStyle}>Assignee</p>
                  <div className="flex items-center gap-1.5">
                    <User className="w-4 h-4 text-gray-400" />
                    <p className={contentStyle} title={jiraConfig.assigneeDisplayName || jiraConfig.assignee}>
                      {jiraConfig.assigneeDisplayName || jiraConfig.assignee}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Workflow Section */}
            {(jiraConfig.startStatus || jiraConfig.endStatus) && (
              <>
                <div className="border-t border-gray-100 dark:border-gray-700/50 pt-4">
                  <p className="font-body-sm text-gray-500 dark:text-gray-400  mb-[0.66rem]">Workflow</p>
                  <div className="flex items-center font-caption  gap-3 flex-wrap">
                    {/* Start Status Pill */}
                    {jiraConfig.startStatus && (
                      <div className="pill bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-yellow-500"></span>
                        {jiraConfig.startStatus}
                      </div>
                    )}

                    {/* Arrow */}
                    {jiraConfig.startStatus && jiraConfig.endStatus && (
                      <ArrowRight strokeWidth={1.5} className="w-4.5 h-4.5 text-gray-400 dark:text-white-400 shrink-0" />
                    )}

                    {/* Agent Pill */}
                    <div className="pill bg-red-100 dark:bg-red-900  text-[#C00F0C] dark:text-red-400">
                      <CpuIcon />
                      Agent resolves
                    </div>

                    {/* Arrow */}
                    {jiraConfig.endStatus && (
                        <ArrowRight strokeWidth={1.5} className="w-4.5 h-4.5 text-gray-400 dark:text-white-400 shrink-0" />
                    )}

                    {/* End Status Pill */}
                    {jiraConfig.endStatus && (
                      <div className="pill bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400">
                        <span className="w-1.5 h-1.5  rounded-full bg-green-500"></span>
                        {jiraConfig.endStatus}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Filter JQL */}
            {jiraConfig.jiraFilterJql && (
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Filter</p>
                <p className="text-sm font-mono text-gray-900 dark:text-white break-all">
                  {jiraConfig.jiraFilterJql}
                </p>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Edit Jira Configuration Modal */}
      {showEditModal && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Edit Jira Configuration
              </h3>
              <button
                onClick={onCloseEditModal}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* Assignee Section */}
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  When the AI agent creates a PR, the Jira ticket will be assigned to this user
                </p>
                <div>
                  <label
                    htmlFor="jiraAssignee"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Default Assignee
                  </label>
                  {isLoadingUsers ? (
                    <div className="flex items-center gap-2 p-3 border border-gray-300 dark:border-gray-600 rounded-lg">
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">Loading users...</span>
                    </div>
                  ) : (
                    <Dropdown
                      options={[
                        { label: 'No assignee', value: '' },
                        ...jiraAssignableUsers
                          .filter(u => u.active)
                          .map(u => ({ label: u.displayName, value: u.accountId }))
                      ]}
                      value={selectedAssigneeAccountId}
                      onChange={onSetSelectedAssigneeAccountId}
                      emptyMessage="No assignable users found"
                    />
                  )}
                </div>
              </div>

              {/* Workflow Statuses Section */}
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  The agent will pick up tickets in the Start Status and move them to the End Status when done. Both must be set together.
                </p>
                {isLoadingStatuses ? (
                  <div className="flex items-center gap-2 p-3 border border-gray-300 dark:border-gray-600 rounded-lg">
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Loading statuses...</span>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label
                        htmlFor="startStatus"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        Start Status
                      </label>
                      <Dropdown
                        options={[
                          { label: 'Not set', value: '' },
                          ...jiraStatuses.map(s => ({ label: s.name, value: s.name }))
                        ]}
                        value={selectedStartStatus}
                        onChange={onSetSelectedStartStatus}
                        emptyMessage="No statuses found"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="endStatus"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                      >
                        End Status
                      </label>
                      <Dropdown
                        options={[
                          { label: 'Not set', value: '' },
                          ...jiraStatuses.map(s => ({ label: s.name, value: s.name }))
                        ]}
                        value={selectedEndStatus}
                        onChange={onSetSelectedEndStatus}
                        emptyMessage="No statuses found"
                      />
                    </div>
                  </div>
                )}
                {validationError && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                    {validationError}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={onCloseEditModal}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={isSaving || isLoadingUsers || isLoadingStatuses || !hasChanges}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center ${
                  !isSaving && !isLoadingUsers && !isLoadingStatuses && hasChanges
                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                    : 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
